import { ConfigurationError } from '@cian/peperrors/shared';
import * as React from 'react';

import { Context } from './context';
import { wrapDisplayName } from '../displayName/shared';
import { IApplicationContext } from '../../types/application';

export type TMapContextToProps<CP> = (context: IApplicationContext) => CP;
export type TMergeProps<P, CP> = (ownProps: Omit<P, keyof CP>, contextProps: CP) => P;

export function withApplicationContext<CP, P extends CP>(
  mapContextToProps: TMapContextToProps<CP>,
  mergeProps?: TMergeProps<P, CP>,
) {
  return (Component: React.ComponentType<P>): React.ComponentType<Omit<P, keyof CP>> => {
    return class WithApplicationContext extends React.Component<P> {
      public static displayName = wrapDisplayName(Component, 'withApplicationContext');

      public render() {
        return (
          <Context.Consumer>
            {context => {
              if (!context) {
                throw new ConfigurationError({
                  message: "withApplicationContext used outside of it's context",
                  domain: WithApplicationContext.displayName,
                });
              }

              const contextProps = mapContextToProps(context);
              const resultProps = (mergeProps && mergeProps(this.props, contextProps)) || {
                ...contextProps,
                ...this.props,
              };

              return <Component {...resultProps} />;
            }}
          </Context.Consumer>
        );
      }
    };
  };
}
